// Droits côté client (socle §4.4, ADR 0022) : permissions effectives et portées viennent de GET /me. Le serveur reste
// la seule autorité ; ces règles ne servent qu'à afficher, masquer ou désactiver (ROL-05, ROL-06).

export type PermissionScope = 'all' | 'assigned' | 'own';

export interface MeLike {
  permissions: string[];
  permission_scopes?: Partial<Record<string, PermissionScope>>;
  primary_role?: { code: string; label?: string | null } | null;
  roles?: Array<{ code: string; label?: string | null }>;
}

/** Permissions utilisées par l'interface : matrice module × action (ADR 0022) et permissions techniques du socle. */
export const PERMISSIONS = {
  // Socle (miroir de PlatformPermissions côté API).
  settingsManage: 'platform.settings.manage',
  templatesManage: 'platform.templates.manage',
  trashManage: 'platform.trash.manage',
  importsViewAll: 'platform.imports.view_all',
  exportsViewAll: 'platform.exports.view_all',
  exportsFull: 'platform.exports.full',
  healthView: 'platform.health.view',
  // Intégrations (lot F17a, miroir de IntegrationsPermissions).
  integrationsManage: 'integrations.manage',
  integrationsView: 'integrations.view',
  // Comptes, rôles (ACC, ROL) : Administrateur T, Direction L + création (§20, ACC-03).
  accountsView: 'accounts.view',
  accountsCreate: 'accounts.create',
  accountsUpdate: 'accounts.update',
  accountsDelete: 'accounts.delete',
  accountsExport: 'accounts.export',
  accountsAdminister: 'accounts.administer',
  // Journal d'audit (AUD) : Administrateur et Direction en portée « tous », les autres profils sur leurs données.
  auditView: 'audit.view',
  auditExport: 'audit.export',
  // Référentiels (REF) : Administrateur T, profils internes L (§20).
  referentialsView: 'referentials.view',
  referentialsCreate: 'referentials.create',
  referentialsUpdate: 'referentials.update',
  referentialsDelete: 'referentials.delete',
  referentialsExport: 'referentials.export',
  // Projets (PRJ) : Administrateur T ; Direction L V + création ; Chef de projet E V* ; Superviseur, Animateur L* ;
  // Comptable L (§20, F3-04). Les actions sur un projet donné suivent `abilities` de la fiche (ROL-06).
  projectsView: 'projects.view',
  projectsCreate: 'projects.create',
  projectsUpdate: 'projects.update',
  projectsDelete: 'projects.delete',
  projectsValidate: 'projects.validate',
  // Activités, jalons, livrables (ACT, JAL) : Administrateur T ; Direction L V ; Chef de projet, Superviseur E V* ;
  // Animateur E* (§20). Planifier = modifier et valider ; l'animateur change le statut de ses tâches (ROL-06, F4-01).
  activitiesView: 'activities.view',
  activitiesCreate: 'activities.create',
  activitiesUpdate: 'activities.update',
  activitiesDelete: 'activities.delete',
  activitiesValidate: 'activities.validate',
  // Collecte et bénéficiaires (COL) : Administrateur T ; Direction L ; Chef de projet L V* ; Superviseur E V* ;
  // Animateur E* (§20). Valident : superviseur de l'activité, chef de projet du projet (ARB-11, F5-01).
  fieldView: 'field.view',
  fieldCreate: 'field.create',
  fieldUpdate: 'field.update',
  fieldDelete: 'field.delete',
  fieldValidate: 'field.validate',
  // Budget et dépenses projet (BUD) : Administrateur T ; Direction L V ; Chef de projet E V* ; Superviseur L* ;
  // Comptable E V ; Animateur aucun accès (§20, écart F6-02). Actions sur une dépense : `abilities` (ROL-06).
  budgetView: 'budget.view',
  budgetCreate: 'budget.create',
  budgetUpdate: 'budget.update',
  budgetDelete: 'budget.delete',
  budgetValidate: 'budget.validate',
  // Rapports (RAP) : Administrateur T ; Direction L V ; Chef de projet E V* ; Superviseur E* ; Animateur E P ;
  // Comptable E ; RH E ; Formateur P (§20). Visibilité RAP-05 et actions sur un rapport : `abilities` (ROL-06).
  reportsView: 'reports.view',
  reportsCreate: 'reports.create',
  reportsValidate: 'reports.validate',
  // Tableaux de bord et analytique (DSH, ANA) : explorateur et exports bruts pour qui a les tableaux de bord.
  dashboardsView: 'dashboards.view',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS] | (string & {});

export function can(me: MeLike | null | undefined, permission: Permission): boolean {
  return me?.permissions.includes(permission) ?? false;
}

export function canAll(me: MeLike | null | undefined, permissions: readonly Permission[]): boolean {
  return permissions.every((p) => can(me, p));
}

export function canAny(me: MeLike | null | undefined, permissions: readonly Permission[]): boolean {
  return permissions.some((p) => can(me, p));
}

/** Portée d'une permission (ROL-06) ; null si elle n'est pas accordée. */
export function scopeOf(me: MeLike | null | undefined, permission: Permission): PermissionScope | null {
  if (!can(me, permission)) return null;
  return me?.permission_scopes?.[permission] ?? 'all';
}

/** Permission accordée avec la portée « tous » (pages globales : journal d'audit, statistiques). */
export function canGlobally(me: MeLike | null | undefined, permission: Permission): boolean {
  return scopeOf(me, permission) === 'all';
}

export function hasRole(me: MeLike | null | undefined, role: string): boolean {
  return me?.roles?.some((r) => r.code === role) ?? me?.primary_role?.code === role;
}
